/**
 * Activity drivers and group targets (FR-PIL-10, FR-PIL-11): sites enter their monthly activity
 * (production volume, headcount, hours worked) so that intensity ratios such as kilograms of
 * hazardous waste per unit produced compare sites; the group sets yearly targets (valorisation
 * rate, reduction of hazardous waste, cost per tonne) and each site's progress is measured.
 */
import { z } from 'zod';
const monthString = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Month must be YYYY-MM');
/** One month of activity of a site; a figure left empty is not known. */
export const activityMonthSchema = z.object({
    month: monthString,
    productionVolume: z.number().min(0).max(1_000_000_000).nullable().optional(),
    headcount: z.number().int().min(0).max(1_000_000).nullable().optional(),
    hours: z.number().min(0).max(100_000_000).nullable().optional(),
});
/** The activity of a site for a year, with the unit its production is counted in. */
export const activityYearSchema = z.object({
    productionUnit: z.string().trim().max(40).nullable().optional(),
    months: z
        .array(activityMonthSchema)
        .max(12)
        .refine((ms) => new Set(ms.map((m) => m.month)).size === ms.length, 'A month is repeated'),
});
/** The targets of the group for a year; a target left empty is not followed. */
export const groupTargetSchema = z.object({
    /** Share of the tonnage shipped to an R operation, 0 to 1. */
    valorisationRate: z.number().min(0).max(1).nullable().optional(),
    /** Reduction of the hazardous waste generated against the same period of the year before. */
    ddReductionRate: z.number().min(-1).max(1).nullable().optional(),
    /** The most a tonne shipped may cost, in MAD. */
    maxCostPerTonne: z.number().min(0).max(1_000_000).nullable().optional(),
});
const known = (values) => values.filter((v) => v !== null);
/** The activity of the months entered: volumes and hours summed, headcount averaged. */
export function activityTotals(months, productionUnit = null) {
    const entered = months.filter((m) => m.productionVolume !== null || m.headcount !== null || m.hours !== null);
    const sum = (values) => {
        const k = known(values);
        return k.length ? Math.round(k.reduce((a, b) => a + b, 0) * 1000) / 1000 : null;
    };
    const heads = known(entered.map((m) => m.headcount));
    return {
        months: entered.length,
        productionVolume: sum(entered.map((m) => m.productionVolume)),
        productionUnit,
        headcount: heads.length
            ? Math.round((heads.reduce((a, b) => a + b, 0) / heads.length) * 10) / 10
            : null,
        hours: sum(entered.map((m) => m.hours)),
    };
}
/**
 * Intensity ratios (FR-PIL-10): what is generated against what is done, month by month, so that
 * a month without activity entered does not inflate the ratio: each ratio takes only the months
 * where its base is known.
 */
export function intensityRatios(rows) {
    const over = (base, tonnes, perBase = 1) => {
        const used = rows.filter((r) => (base(r) ?? 0) > 0);
        const b = used.reduce((s, r) => s + base(r), 0) / perBase;
        if (!b)
            return null;
        const kg = used.reduce((s, r) => s + tonnes(r), 0) * 1000;
        return Math.round((kg / b) * 100) / 100;
    };
    return {
        kgDdPerUnit: over((r) => r.productionVolume, (r) => r.ddT),
        kgPerUnit: over((r) => r.productionVolume, (r) => r.totalT),
        kgPerEmployeeMonth: over((r) => r.headcount, (r) => r.totalT),
        kgPerThousandHours: over((r) => r.hours, (r) => r.totalT, 1000),
    };
}
/**
 * The intensity of several sites together. The ratios per unit produced exist only when every
 * site with a production entered counts it in the same unit.
 */
export function combinedIntensity(sites) {
    const ratios = intensityRatios(sites.flatMap((s) => s.rows));
    const producing = sites.filter((s) => s.rows.some((r) => (r.productionVolume ?? 0) > 0));
    const units = new Set(producing.map((s) => (s.productionUnit ?? '').trim().toLowerCase()));
    if (units.size === 1) {
        return { ...ratios, productionUnit: producing[0].productionUnit?.trim() || null };
    }
    return { ...ratios, kgDdPerUnit: null, kgPerUnit: null, productionUnit: null };
}
/** The same dates one year earlier (29 February becomes 28 February). */
export function samePeriodLastYear(from, to) {
    const back = (d) => {
        const md = d.slice(5) === '02-29' ? '02-28' : d.slice(5);
        return `${Number(d.slice(0, 4)) - 1}-${md}`;
    };
    return { from: back(from), to: back(to) };
}
/**
 * The progress of a site, or of the group, against the targets (FR-PIL-11). The reduction of
 * hazardous waste compares the period with the same period of the year before; it is unknown
 * when nothing was generated then.
 */
export function targetProgress(target, figures) {
    const check = (goal, actual, better) => {
        if (goal === null || goal === undefined)
            return null;
        return {
            actual,
            target: goal,
            met: actual === null ? null : better === 'higher' ? actual >= goal : actual <= goal,
        };
    };
    return {
        valorisationRate: check(target?.valorisationRate, figures.valorisationRate, 'higher'),
        ddReductionRate: check(target?.ddReductionRate, ddReduction(figures), 'higher'),
        maxCostPerTonne: check(target?.maxCostPerTonne, figures.costPerTonne, 'lower'),
    };
}
/** 1 − current / previous, rounded to a tenth of a percent; null without a previous figure. */
export function ddReduction(f) {
    return f.ddGeneratedPreviousT > 0
        ? Math.round((1 - f.ddGeneratedT / f.ddGeneratedPreviousT) * 1000) / 1000
        : null;
}
//# sourceMappingURL=targets.js.map